/**
 * The API client.
 *
 * Note what is absent: no tenant id anywhere. The session cookie decides which
 * tenant a request belongs to, so the frontend has no way to ask for the wrong
 * one — the isolation model from Phase 10, visible here as a missing parameter.
 */

export type ValueFormat = "number" | "currency" | "percent";
export type WidgetType = "card" | "line" | "bar" | "table";

export interface Point {
  label: string;
  value: number | null;
  period_start: string | null;
}

export interface Widget {
  type: WidgetType;
  title: string;
  subtitle: string | null;
  format: ValueFormat;
  /** The metrics behind this widget, so a drill can recompute the same numbers. */
  metrics: string[];
  period_type: string | null;
  /** Set when the widget is a breakdown; tells a click which field to filter. */
  dimension: string | null;
  /** "all" or "mtd". An "mtd" card is permanent and ignores the toolbar range. */
  window: string;
  value: number | null;
  series: Record<string, Point[]>;
}

export interface Dimension {
  field: string;
  label: string;
}

export interface MetricInfo {
  name: string;
  label: string;
  format: ValueFormat;
}

export interface Dashboard {
  dataset: string;
  dimensions: Dimension[];
  metrics: MetricInfo[];
  period_field: string | null;
  date_min: string | null;
  date_max: string | null;
  /** The month-to-date window, for the breakdown's MTD column. */
  mtd_from: string | null;
  mtd_to: string | null;
  widgets: Widget[];
}

/** A drill filter. Mirrors the backend's QueryFilter exactly. */
export interface Filter {
  field: string;
  op: "eq" | "ne" | "gt" | "gte" | "lt" | "lte" | "in";
  value: string | number | boolean | null | (string | number)[];
  /** What to show on the chip — the field's human label, not its name. */
  label?: string;
}

export interface QueryRow {
  group: string | null;
  period_start: string | null;
  values: Record<string, number | null>;
  row_count: number;
}

export interface DetailResult {
  columns: string[];
  rows: Record<string, unknown>[];
  total: number;
  offset: number;
}

export interface Me {
  email: string;
  tenant_id: string;
  tenant_name: string;
  is_operator: boolean;
  /** Set only while viewing as someone else, e.g. "jane@acme.com @ Acme Corp". */
  impersonating?: string | null;
}

export interface DatasetSummary {
  dataset: string;
  row_count: number;
  last_updated: string | null;
}

/** One thing that needs a human's eye, across all tenants (operator only). */
export interface Anomaly {
  id: string;
  tenant: string;
  tenant_slug: string;
  dataset: string;
  kind: string;
  severity: "warning" | "error";
  message: string;
  detail: Record<string, unknown>;
  detected_at: string;
}

/** One file's trip through the pipeline (operator only). */
export interface PipelineRun {
  tenant: string;
  tenant_slug: string;
  dataset: string;
  source: string;
  rows_parsed: number;
  inserted: number;
  updated: number;
  unchanged: number;
  deleted: number;
  skipped: number;
  issues: number;
  ok: boolean;
  error: string | null;
  started_at: string;
}

export interface ClientUser {
  id: string;
  email: string;
  display_name: string | null;
  is_operator: boolean;
  is_active: boolean;
  last_login_at: string | null;
}

export interface ClientSummary {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  is_active: boolean;
  records: number;
  datasets: number;
  last_ingest: string | null;
  users: ClientUser[];
}

export interface OperatorHealth {
  open_errors: number;
  open_warnings: number;
  failed_runs_24h: number;
  anomalies: Anomaly[];
  runs: PipelineRun[];
}

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    // Send the session cookie. Without this the browser omits it and every
    // request looks unauthenticated.
    credentials: "same-origin",
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    throw new ApiError(detail?.detail ?? response.statusText, response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  login: (email: string, password: string) =>
    request<Me>("/api/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>("/api/logout", { method: "POST" }),
  me: () => request<Me>("/api/me"),
  datasets: () => request<DatasetSummary[]>("/api/datasets"),
  dashboard: (dataset: string) =>
    request<Dashboard>(`/api/dashboard?dataset=${encodeURIComponent(dataset)}`),

  /** Distinct values of a dimension, for the search dropdown. */
  dimensionValues: (dataset: string, field: string) =>
    request<string[]>(
      `/api/dimension-values?dataset=${encodeURIComponent(dataset)}&field=${encodeURIComponent(field)}`,
    ),

  /** Live aggregation. Used the moment any filter is applied. */
  query: (body: {
    dataset: string;
    filters?: Filter[];
    group_by?: string | null;
    period_type?: string | null;
    metrics?: string[];
    limit?: number;
  }) =>
    request<QueryRow[]>("/api/query", {
      method: "POST",
      // The chip label is presentation-only; the API rejects unknown fields.
      body: JSON.stringify({
        ...body,
        filters: (body.filters ?? []).map(({ field, op, value }) => ({ field, op, value })),
      }),
    }),

  detail: (body: {
    dataset: string;
    filters?: Filter[];
    sort?: string | null;
    descending?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    request<DetailResult>("/api/detail", {
      method: "POST",
      body: JSON.stringify({
        ...body,
        filters: (body.filters ?? []).map(({ field, op, value }) => ({ field, op, value })),
      }),
    }),

  /** Download every matching row as a CSV file. */
  exportDetail: async (body: {
    dataset: string;
    filters?: Filter[];
    sort?: string | null;
    descending?: boolean;
  }) => {
    const response = await fetch("/api/detail/export", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        dataset: body.dataset,
        sort: body.sort ?? null,
        descending: body.descending ?? true,
        filters: (body.filters ?? []).map(({ field, op, value }) => ({ field, op, value })),
      }),
    });
    if (!response.ok) throw new ApiError("export failed", response.status);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${body.dataset}_export.csv`;
    link.click();
    URL.revokeObjectURL(url);
  },

  /** Operator dashboard: open anomalies and recent runs across every tenant. */
  operatorHealth: () => request<OperatorHealth>("/api/operator/health"),

  /** Acknowledge an anomaly so it leaves the open list. */
  resolveAnomaly: (id: string) =>
    request<void>(`/api/operator/anomalies/${encodeURIComponent(id)}/resolve`, {
      method: "POST",
    }),

  /** Every client, their users and what data they hold (operator only). */
  clients: () => request<ClientSummary[]>("/api/operator/clients"),

  /** Start viewing as another user. The session's effective user changes. */
  impersonate: (userId: string) =>
    request<void>("/api/operator/impersonate", {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    }),

  stopImpersonating: () =>
    request<void>("/api/operator/impersonate/stop", { method: "POST" }),
};

export { ApiError };

/**
 * How much precision a number is shown with.
 *
 * "axis" is the ONLY mode that abbreviates. Abbreviating a headline figure is
 * how $3,675,875.97 becomes "$4M" — a number that is wrong by three hundred
 * thousand dollars and looks authoritative. Headline and table values are never
 * abbreviated; only the chart's own gridline labels are, where the exact value
 * is available by hovering a point anyway.
 */
export type Precision = "full" | "short" | "axis";

/** Format a number for display, driven entirely by the config's `format`. */
export function formatValue(
  value: number | null,
  format: ValueFormat,
  precision: Precision = "full",
): string {
  if (value === null || value === undefined) return "—";
  switch (format) {
    case "currency":
      return value.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        // "short" drops the cents on figures where they are noise, but keeps
        // every significant digit.
        maximumFractionDigits: precision === "full" ? 2 : 0,
        notation: precision === "axis" && Math.abs(value) >= 10_000 ? "compact" : "standard",
      });
    case "percent":
      return `${value.toFixed(1)}%`;
    default:
      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
}
